import { isAxiosError } from 'axios'

import { apiClient } from '@/api/client'
import { endpoints } from '@/api/endpoints'
import type { ApiResponse } from '@/api/types'
import type { Customer } from '@/api/types'
import type {
  DeliveryChallanDocument,
  DeliveryChallanLine,
  DeliveryChallanRow,
} from '@/modules/inventory/delivery-challan/types'
import { newDeliveryChallanLine } from '@/modules/inventory/delivery-challan/types'
import { batchItemService } from '@/services/batchItemService'
import { customersService } from '@/services/customersService'
import { itemService } from '@/services/itemService'
import { salesOrderService } from '@/services/salesOrderService'

export type DcCustomerOption = { id: number; name: string }

export type DcOrderOption = {
  id: number
  /** Sales order code only (dropdown display) */
  label: string
  code: string
  reference: string
  warehouseName: string
  status: string
}

export type DcOrderLineRaw = { itemId: number; batchId: number; quantity: number; lineId: number }

export type DcLineLookupExtras = {
  itemExtras: Array<{ id: number; name: string }>
  batchExtrasByItem: Record<string, Array<{ id: number; name: string; stock: number }>>
}

/** Multipart master + lines JSON for POST /api/Dc/Save */
export type DcSavePayload = {
  id: number
  customerId: number
  orderId: number
  /** Challan date (yyyy-mm-dd or ISO) */
  date: string
  /** Expected / supply date (yyyy-mm-dd or ISO) */
  supplyDate: string
  branchId: number
  attachmentFile: File | null
  lines: DeliveryChallanLine[]
}

function appendDcFormField(formData: FormData, key: string, value: unknown) {
  if (value === undefined || value === null) {
    formData.append(key, '')
    return
  }
  formData.append(key, String(value))
}

function dcChallanDateToIso(dateStr: string): string {
  const s = dateStr?.trim() ?? ''
  if (!s) return new Date().toISOString()
  if (s.includes('T')) return s
  return `${s}T00:00:00.000Z`
}

/**
 * One object inside `dcLinesJson` — must match Swagger `DcLines` / sample `dcLinesJson` exactly:
 * id, orderLineId, itemId, bratchId (API spelling), quantityReceived, quantityReturned, isDeleted.
 */
function normalizeDcLineForJson(l: DeliveryChallanLine): Record<string, number | string> {
  const itemId = Number(l.itemId) || 0
  const bratchId = Number(l.batchId) || 0
  const orderLineIdVal =
    l.fromOrder && (Number(l.orderLineId ?? l.id) > 0)
      ? Number(l.orderLineId ?? l.id) || 0
      : 0
  const dcLinePk = Number(l.dcLineId ?? 0) || 0
  return {
    id: dcLinePk,
    orderLineId: orderLineIdVal,
    itemId,
    bratchId,
    quantityReceived: Number(l.quantityReceived) || 0,
    quantityReturned: Number(l.quantityReturn) || 0,
    isDeleted: 'false',
  }
}

function buildDcLinesJson(lines: DeliveryChallanLine[]): string {
  return JSON.stringify(lines.filter((l) => !l.isBonus).map(normalizeDcLineForJson))
}

function buildDcSaveFormData(payload: DcSavePayload, dcLinesJson: string): FormData {
  const formData = new FormData()
  appendDcFormField(formData, 'Id', payload.id)
  appendDcFormField(formData, 'CustomerId', payload.customerId)
  appendDcFormField(formData, 'OrderId', payload.orderId)
  appendDcFormField(formData, 'Date', dcChallanDateToIso(payload.date))
  appendDcFormField(formData, 'SupplyDate', dcChallanDateToIso(payload.supplyDate))
  appendDcFormField(formData, 'BranchId', payload.branchId)
  appendDcFormField(formData, 'dcLinesJson', dcLinesJson)
  if (payload.attachmentFile) {
    formData.append('AttachmentFile', payload.attachmentFile)
  } else {
    appendDcFormField(formData, 'AttachmentFile', '')
  }
  return formData
}

/** Readable snapshot of FormData for console debugging (file → metadata only). */
function snapshotDcSaveFormData(fd: FormData): Record<string, string | Record<string, unknown>> {
  const out: Record<string, string | Record<string, unknown>> = {}
  fd.forEach((value, key) => {
    if (value instanceof File) {
      out[key] = { fileName: value.name, size: value.size, type: value.type }
    } else {
      out[key] = String(value)
    }
  })
  return out
}

/** Same result envelope pattern as GRN / other inventory saves */
function throwIfDcResultError(data: unknown) {
  if (!data || typeof data !== 'object') return
  const root = data as Record<string, unknown>
  const r = root.result ?? root.Result
  if (!r || typeof r !== 'object') return
  const o = r as Record<string, unknown>
  if (o.isError !== true && o.IsError !== true) return
  const msg =
    (typeof o.msg === 'string' && o.msg.trim()) ||
    (typeof o.Msg === 'string' && o.Msg.trim()) ||
    'Operation failed.'
  const err = new Error(msg) as Error & { response?: { status: number; data: unknown } }
  err.response = { status: 400, data }
  throw err
}

/** Shared POST multipart for `/api/Dc/Save` and `/api/Dc/Update` (same body shape; Update requires Id > 0). */
async function postDcMultipart(url: string, logTag: string, payload: DcSavePayload): Promise<void> {
  const dcLinesJson = buildDcLinesJson(payload.lines)
  const dateIso = dcChallanDateToIso(payload.date)
  const supplyIso = dcChallanDateToIso(payload.supplyDate)
  const linesParsed = payload.lines.map(normalizeDcLineForJson)

  const formData = buildDcSaveFormData(payload, dcLinesJson)
  const formSnapshot = snapshotDcSaveFormData(formData)

  // eslint-disable-next-line no-console
  console.groupCollapsed(
    `%c[${logTag}] Outgoing request`,
    'background:#1d4ed8;color:#fff;font-weight:bold;padding:2px 8px;border-radius:4px',
  )
  // eslint-disable-next-line no-console
  console.log('URL:', url)
  // eslint-disable-next-line no-console
  console.log('Structure (logical multipart fields):', {
    Id: payload.id,
    CustomerId: payload.customerId,
    OrderId: payload.orderId,
    Date: dateIso,
    SupplyDate: supplyIso,
    BranchId: payload.branchId,
    dcLinesJson,
    dcLinesParsed: linesParsed,
    AttachmentFile: payload.attachmentFile
      ? {
          name: payload.attachmentFile.name,
          size: payload.attachmentFile.size,
          type: payload.attachmentFile.type,
        }
      : '(empty — field sent as "")',
    sourceLinesFromUi: payload.lines,
  })
  // eslint-disable-next-line no-console
  console.log('FormData snapshot (key → value as sent):', formSnapshot)
  // eslint-disable-next-line no-console
  console.groupEnd()

  try {
    const res = await apiClient.post(url, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    // eslint-disable-next-line no-console
    console.groupCollapsed(
      `%c[${logTag}] API response (success HTTP)`,
      'background:#15803d;color:#fff;font-weight:bold;padding:2px 8px;border-radius:4px',
    )
    // eslint-disable-next-line no-console
    console.log('HTTP status:', res.status)
    // eslint-disable-next-line no-console
    console.log('response.data (exact):', res.data)
    try {
      // eslint-disable-next-line no-console
      console.log('response.data (JSON):', JSON.stringify(res.data, null, 2))
    } catch {
      // eslint-disable-next-line no-console
      console.log('response.data (JSON): <could not stringify>')
    }
    // eslint-disable-next-line no-console
    console.groupEnd()
    throwIfDcResultError(res.data)
  } catch (err: unknown) {
    // eslint-disable-next-line no-console
    console.groupCollapsed(
      `%c[${logTag}] API response (error)`,
      'background:#b91c1c;color:#fff;font-weight:bold;padding:2px 8px;border-radius:4px',
    )
    if (isAxiosError(err)) {
      // eslint-disable-next-line no-console
      console.log('axios message:', err.message)
      // eslint-disable-next-line no-console
      console.log('HTTP status:', err.response?.status)
      // eslint-disable-next-line no-console
      console.log('response.data (exact):', err.response?.data)
      try {
        // eslint-disable-next-line no-console
        console.log('response.data (JSON):', JSON.stringify(err.response?.data, null, 2))
      } catch {
        // eslint-disable-next-line no-console
        console.log('response.data (JSON): <could not stringify>')
      }
    } else if (err instanceof Error) {
      // eslint-disable-next-line no-console
      console.log('Error:', err.message, err)
      const ext = err as Error & { response?: { data?: unknown } }
      if (ext.response?.data !== undefined) {
        // eslint-disable-next-line no-console
        console.log('response.data (e.g. result.isError body):', ext.response.data)
        try {
          // eslint-disable-next-line no-console
          console.log('response.data (JSON):', JSON.stringify(ext.response.data, null, 2))
        } catch {
          /* ignore */
        }
      }
    } else {
      // eslint-disable-next-line no-console
      console.log('Unknown:', err)
    }
    // eslint-disable-next-line no-console
    console.groupEnd()
    throw err
  }
}

function unwrap(payload: unknown) {
  if (payload && typeof payload === 'object') {
    const o = payload as Record<string, unknown>
    if ('success' in o && 'data' in o) return o.data
    if ('data' in o) return o.data
  }
  return payload
}

/** Prefer known array property names from .NET / Swagger payloads */
function firstArrayProperty(o: Record<string, unknown>): unknown[] | null {
  const keys = [
    'data',
    'items',
    'deliveryChallans',
    'challans',
    'dcRecords',
    'orderLines',
    'lines',
    'lineItems',
    'salesOrderLines',
    'result',
    'records',
    'rows',
    'values',
  ] as const
  for (const k of keys) {
    const v = o[k]
    if (Array.isArray(v)) return v
  }
  return null
}

function toArray(payload: unknown): unknown[] {
  const raw = unwrap(payload)
  if (Array.isArray(raw)) return raw
  if (raw && typeof raw === 'object') {
    const o = raw as Record<string, unknown>
    const nested = firstArrayProperty(o)
    if (nested) return nested
    return [raw]
  }
  return []
}

function dateOnlyFromApi(value: unknown): string {
  const s = String(value ?? '')
  if (!s) return ''
  return s.includes('T') ? s.split('T')[0] : s
}

type DcListRowDraft = {
  id: number
  code: string
  customerId: number
  orderId: number
  challanDate: string
  expectedDeliveryDate: string
  createdDateIso: string
  isInvoiceCreated: boolean
}

function apiTruthyBool(v: unknown): boolean {
  if (v === true || v === 1 || v === '1') return true
  if (v === false || v === 0 || v === '0' || v === '' || v == null) return false
  if (typeof v === 'string') return v.toLowerCase() === 'true'
  return Boolean(v)
}

function mapDcListRowDraft(x: Record<string, unknown>): DcListRowDraft {
  const dateRaw = x?.date ?? x?.Date ?? x?.challanDate ?? x?.ChallanDate
  const supplyRaw =
    x?.supplyDate ??
    x?.SupplyDate ??
    x?.expectedDeliveryDate ??
    x?.ExpectedDeliveryDate ??
    x?.estDeliveryDate ??
    x?.EstDeliveryDate
  const createdRaw = x?.createdDate ?? x?.CreatedDate ?? ''
  const invRaw = x?.isInvoiceCreated ?? x?.IsInvoiceCreated
  return {
    id: Number(x?.id ?? x?.Id ?? 0),
    code: String(x?.code ?? x?.Code ?? ''),
    customerId: Number(x?.customerId ?? x?.CustomerId ?? 0),
    orderId: Number(x?.orderId ?? x?.OrderId ?? 0),
    challanDate: dateOnlyFromApi(dateRaw),
    expectedDeliveryDate: dateOnlyFromApi(supplyRaw),
    createdDateIso: String(createdRaw ?? '').trim(),
    isInvoiceCreated: apiTruthyBool(invRaw),
  }
}

function customerDisplayName(c: Customer): string {
  const en = c.customerNameEn?.trim()
  const ar = c.customerNameAr?.trim()
  return (en || ar || (typeof c.name === 'string' ? c.name.trim() : '') || '').trim()
}

function mapDcLineFromApiRow(row: Record<string, unknown>): DeliveryChallanLine {
  const dcLinePk = Number(row?.id ?? row?.Id ?? 0)
  const orderLineId = Number(row?.orderLineId ?? row?.OrderLineId ?? 0)
  const itemId = Number(row?.itemId ?? row?.ItemId ?? 0)
  const batchId = Number(
    row?.batchId ??
      row?.BatchId ??
      row?.bratchId ??
      row?.BratchId ??
      row?.batchItemId ??
      row?.BatchItemId ??
      0,
  )
  const qtyRec = Number(
    row?.quantityReceived ?? row?.QuantityReceived ?? row?.qtyReceived ?? row?.QtyReceived ?? 0,
  )
  const qtyRet = Number(
    row?.quantityReturned ??
      row?.QuantityReturned ??
      row?.quantityReturn ??
      row?.QuantityReturn ??
      0,
  )
  const fromOrder = orderLineId > 0
  const avail = qtyRec + qtyRet
  const isBonus = apiTruthyBool(row?.isBonus ?? row?.IsBonus)
  return {
    id: fromOrder ? orderLineId : 0,
    dcLineId: dcLinePk > 0 ? dcLinePk : undefined,
    orderLineId: orderLineId > 0 ? orderLineId : undefined,
    itemId: String(itemId),
    batchId: String(batchId),
    availableQuantity: avail > 0 ? avail : qtyRec,
    quantityReceived: qtyRec,
    quantityReturn: qtyRet,
    fromOrder,
    isBonus,
  }
}

function sortDcLinesWithBonus(lines: DeliveryChallanLine[]): DeliveryChallanLine[] {
  const sold = lines.filter((l) => !l.isBonus)
  const bonus = lines.filter((l) => l.isBonus)
  const used = new Set<DeliveryChallanLine>()
  const out: DeliveryChallanLine[] = []
  for (const s of sold) {
    out.push(s)
    const parentId = Number(s.orderLineId ?? s.id)
    for (const b of bonus) {
      if (used.has(b)) continue
      if (Number(b.orderLineId) === parentId) {
        out.push(b)
        used.add(b)
      }
    }
  }
  for (const b of bonus) {
    if (!used.has(b)) out.push(b)
  }
  return out
}

function attachmentBasename(path: string): string {
  const s = path.replace(/\\/g, '/').trim()
  if (!s) return ''
  const parts = s.split('/')
  return parts[parts.length - 1] || s
}

async function buildDocumentFromMasterAndLines(
  master: Record<string, unknown>,
  lineRows: unknown[],
  branchId: number,
): Promise<DeliveryChallanDocument> {
  const dcId = Number(master?.id ?? master?.Id ?? 0)
  const orderIdNum = Number(master?.orderId ?? master?.OrderId ?? 0)
  const customerIdNum = Number(master?.customerId ?? master?.CustomerId ?? 0)
  const dateRaw = master?.date ?? master?.Date
  const supplyRaw = master?.supplyDate ?? master?.SupplyDate
  const attachment = String(
    master?.attachment ?? master?.Attachment ?? master?.attachmentPath ?? master?.AttachmentPath ?? '',
  )

  let orderCode = ''
  let orderReference = ''
  let orderWarehouseName = ''
  let orderStatus = ''
  let customerName = ''

  await Promise.all([
    orderIdNum > 0
      ? salesOrderService
          .getSalesOrderById(orderIdNum)
          .then((so) => {
            orderCode = so.code?.trim() || String(orderIdNum)
            orderReference = so.reference?.trim() || ''
            orderWarehouseName = so.warehouseName?.trim() || ''
            orderStatus = String(so.orderStatus ?? '')
          })
          .catch(() => {
            orderCode = String(orderIdNum)
          })
      : Promise.resolve(),
    customerIdNum > 0
      ? customersService
          .getCustomerById(branchId, customerIdNum)
          .then((c) => {
            customerName = customerDisplayName(c) || `Customer ${customerIdNum}`
          })
          .catch(() => {
            customerName = ''
          })
      : Promise.resolve(),
  ])

  const lines: DeliveryChallanLine[] = sortDcLinesWithBonus(
    lineRows
      .filter((x) => x && typeof x === 'object')
      .map((x) => mapDcLineFromApiRow(x as Record<string, unknown>))
      .filter((l) => Number(l.itemId) > 0 && Number(l.batchId) > 0),
  )

  const filledLines =
    lines.length > 0 ? lines : Array.from({ length: 3 }, () => newDeliveryChallanLine())

  const total = filledLines.reduce(
    (sum, l) => sum + Number(l.quantityReceived || 0) + Number(l.quantityReturn || 0),
    0,
  )

  return {
    id: dcId,
    code: String(master?.code ?? master?.Code ?? ''),
    orderId: orderIdNum > 0 ? String(orderIdNum) : '',
    orderCode,
    orderReference,
    orderWarehouseName,
    orderStatus,
    customerId: customerIdNum > 0 ? String(customerIdNum) : '',
    customerName,
    challanDate: dateOnlyFromApi(dateRaw),
    expectedDeliveryDate: dateOnlyFromApi(supplyRaw),
    attachmentFileName: attachmentBasename(attachment),
    attachmentFilePath: attachment,
    total,
    lines: filledLines,
  }
}

async function enrichDcListRows(branchId: number, drafts: DcListRowDraft[]): Promise<DeliveryChallanRow[]> {
  const customerIds = [...new Set(drafts.map((d) => d.customerId).filter((id) => id > 0))]
  const orderIds = [...new Set(drafts.map((d) => d.orderId).filter((id) => id > 0))]

  const customerMap = new Map<number, string>()
  await Promise.all(
    customerIds.map(async (id) => {
      try {
        const c = await customersService.getCustomerById(branchId, id)
        const name = customerDisplayName(c)
        customerMap.set(id, name)
      } catch {
        customerMap.set(id, '')
      }
    }),
  )

  const orderMap = new Map<number, string>()
  await Promise.all(
    orderIds.map(async (id) => {
      try {
        const so = await salesOrderService.getSalesOrderById(id)
        orderMap.set(id, (so.code && so.code.trim()) || String(id))
      } catch {
        orderMap.set(id, '')
      }
    }),
  )

  return drafts.map((d) => ({
    id: d.id,
    code: d.code,
    customerName:
      d.customerId > 0 ? customerMap.get(d.customerId) || '—' : '—',
    orderCode: d.orderId > 0 ? orderMap.get(d.orderId) || '—' : '—',
    challanDate: d.challanDate,
    expectedDeliveryDate: d.expectedDeliveryDate,
    createdDate: d.createdDateIso,
    isInvoiceCreated: d.isInvoiceCreated,
  }))
}

function mapDcOrderLineRow(row: Record<string, unknown>): DcOrderLineRaw {
  const itemId = Number(
    row?.itemId ??
      row?.ItemId ??
      row?.itemID ??
      row?.ItemID ??
      row?.productId ??
      row?.ProductId ??
      0,
  )
  /** Backend often exposes batch line id as batchItemId (BatchItem PK), not batchId */
  const batchId = Number(
    row?.batchId ??
      row?.BatchId ??
      row?.batchID ??
      row?.batchItemId ??
      row?.BatchItemId ??
      row?.batchitemId ??
      0,
  )
  const quantity = Number(
    row?.quantity ??
      row?.Quantity ??
      row?.qty ??
      row?.Qty ??
      row?.orderedQuantity ??
      row?.OrderedQuantity ??
      row?.orderQuantity ??
      row?.OrderQuantity ??
      0,
  )
  /** Line row id from API (`id` on the line DTO; may match orderId on some backends) */
  const lineId = Number(
    row?.salesOrderLineId ?? row?.SalesOrderLineId ?? row?.lineId ?? row?.LineId ?? row?.id ?? row?.Id ?? 0,
  )
  return { itemId, batchId, quantity, lineId }
}

async function resolveItemAndBatchNames(
  itemId: number,
  batchId: number,
  branchId: number,
  fallbackAvail: number,
): Promise<{ itemName: string; batchName: string; availableQuantity: number }> {
  let itemName = `Item ${itemId}`
  let batchName = `Batch ${batchId}`
  let availableQuantity = fallbackAvail
  try {
    const item = await itemService.getItemById(itemId, branchId)
    itemName =
      item.itemNameEn?.trim() ||
      item.itemNameAr?.trim() ||
      item.itemCode?.trim() ||
      itemName
  } catch {
    /* use fallback */
  }
  try {
    const batch = await batchItemService.getById(batchId)
    batchName = batch.code?.trim() || batch.reference?.trim() || batchName
    availableQuantity = batch.availableQuantity ?? availableQuantity
  } catch {
    /* use fallback */
  }
  return { itemName, batchName, availableQuantity }
}

export const deliveryChallanService = {
  /**
   * GET `/api/Dc/GetAll` — paginated list for branch.
   */
  async getAll(
    branchId: number,
    pageSize: number,
    pageNumber: number,
  ): Promise<{ items: DeliveryChallanRow[]; totalCount: number }> {
    const response = await apiClient.get<ApiResponse<unknown> | unknown>(
      endpoints.dc.getAll(branchId, pageSize, pageNumber),
    )
    const payload = response.data
    const top =
      payload && typeof payload === 'object' && !Array.isArray(payload)
        ? (payload as Record<string, unknown>)
        : null
    const raw = unwrap(payload)

    let list: unknown[] = toArray(raw)
    if (list.length === 0 && raw && typeof raw === 'object' && !Array.isArray(raw)) {
      const r = raw as Record<string, unknown>
      if (Array.isArray(r.items)) list = r.items
    }
    if (list.length === 0 && top && Array.isArray(top.items)) {
      list = top.items as unknown[]
    }
    if (list.length === 0 && top && Array.isArray(top.data)) {
      list = top.data as unknown[]
    }

    const totalCount = Number(
      (raw && typeof raw === 'object' && !Array.isArray(raw)
        ? (raw as Record<string, unknown>).totalRecords ?? (raw as Record<string, unknown>).totalCount
        : undefined) ??
        top?.totalRecords ??
        top?.totalCount ??
        list.length,
    )

    const drafts = list
      .filter((row) => row && typeof row === 'object')
      .map((row) => mapDcListRowDraft(row as Record<string, unknown>))
    const items = await enrichDcListRows(branchId, drafts)
    const tc = Number.isFinite(totalCount) && totalCount >= 0 ? totalCount : items.length
    return { items, totalCount: tc }
  },

  /**
   * GET `/api/Dc/GetById?id=` — delivery challan master (edit screen).
   */
  async getMasterById(id: number): Promise<Record<string, unknown>> {
    const response = await apiClient.get<ApiResponse<unknown> | unknown>(endpoints.dc.getById(id))
    const raw = unwrap(response.data)
    if (raw && typeof raw === 'object' && !Array.isArray(raw)) return raw as Record<string, unknown>
    return {}
  },

  /**
   * GET `/api/Dc/GetDcLinesByDcId?dcId=` — lines for that challan.
   */
  async getDcLinesByDcId(dcId: number): Promise<unknown[]> {
    const response = await apiClient.get<ApiResponse<unknown> | unknown>(
      endpoints.dc.getDcLinesByDcId(dcId),
    )
    return toArray(response.data)
  },

  /**
   * Parallel master + lines, then customer name and sales order snapshot for the form.
   */
  async loadChallanForEdit(dcId: number, branchId: number): Promise<DeliveryChallanDocument> {
    const [master, lineRows] = await Promise.all([this.getMasterById(dcId), this.getDcLinesByDcId(dcId)])
    const mid = Number(master?.id ?? master?.Id ?? 0)
    if (!mid) {
      const err = new Error('Delivery challan not found.') as Error & { response?: { status: number } }
      err.response = { status: 404 }
      throw err
    }
    return buildDocumentFromMasterAndLines(master, lineRows, branchId)
  },

  async getCustomers(branchId: number): Promise<DcCustomerOption[]> {
    const response = await apiClient.get<ApiResponse<unknown> | unknown>(endpoints.dc.getCustomers(branchId))
    const arr = toArray(response.data)
    return arr
      .map((x: unknown) => {
        const row = x as Record<string, unknown>
        return {
          id: Number(row?.customerId ?? row?.CustomerId ?? row?.id ?? row?.Id ?? 0),
          name: String(
            row?.customerName ?? row?.CustomerName ?? row?.customerNameEn ?? row?.name ?? row?.Name ?? '',
          ).trim(),
        }
      })
      .filter((c) => c.id > 0 && c.name)
  },

  async getOrders(branchId: number, customerId: number): Promise<DcOrderOption[]> {
    if (!customerId) return []
    const response = await apiClient.get<ApiResponse<unknown> | unknown>(
      endpoints.dc.getOrders(branchId, customerId),
    )
    const arr = toArray(response.data)
    return arr
      .map((x: unknown) => {
        const row = x as Record<string, unknown>
        const id = Number(row?.id ?? row?.Id ?? row?.orderId ?? row?.OrderId ?? row?.salesOrderId ?? 0)
        const code = String(row?.code ?? row?.Code ?? '').trim()
        const reference = String(row?.reference ?? row?.Reference ?? '').trim()
        const warehouseName = String(
          row?.warehouseName ?? row?.WarehouseName ?? row?.warehouse ?? '',
        ).trim()
        const status = String(
          row?.orderStatus ?? row?.OrderStatus ?? row?.status ?? row?.Status ?? row?.orderStatusName ?? '',
        ).trim()
        const label = code || String(id)
        return { id, label, code, reference: reference || code, warehouseName, status }
      })
      .filter((o) => o.id > 0)
  },

  async getOrderLines(orderId: number): Promise<DcOrderLineRaw[]> {
    if (!orderId) return []
    const response = await apiClient.get<ApiResponse<unknown> | unknown>(endpoints.dc.getOrderLines(orderId))
    const arr = toArray(response.data)
    return arr
      .filter((x) => x && typeof x === 'object')
      .map((x: unknown) => mapDcOrderLineRow(x as Record<string, unknown>))
      .filter((l) => l.itemId > 0 && l.batchId > 0)
  },

  /**
   * Load order lines from Dc/GetOrderLines, resolve item name (ApiItem/GetItemsById) and batch code (BatchItem/GetBatchItemById).
   */
  async hydrateOrderLinesForChallan(
    orderId: number,
    branchId: number,
  ): Promise<{ lines: DeliveryChallanLine[] } & DcLineLookupExtras> {
    const raw = await this.getOrderLines(orderId)
    const itemExtrasMap = new Map<number, string>()
    const batchExtrasByItem: DcLineLookupExtras['batchExtrasByItem'] = {}

    const resolved = await Promise.all(
      raw.map(async (r) => {
        const names = await resolveItemAndBatchNames(r.itemId, r.batchId, branchId, 0)
        return { r, names }
      }),
    )

    const lines: DeliveryChallanLine[] = []
    for (const { r, names } of resolved) {
      if (!itemExtrasMap.has(r.itemId)) itemExtrasMap.set(r.itemId, names.itemName)
      const ikey = String(r.itemId)
      if (!batchExtrasByItem[ikey]) batchExtrasByItem[ikey] = []
      if (!batchExtrasByItem[ikey].some((b) => b.id === r.batchId)) {
        batchExtrasByItem[ikey].push({
          id: r.batchId,
          name: names.batchName,
          stock: r.quantity,
        })
      }
      const orderQty = r.quantity
      lines.push({
        id: r.lineId,
        itemId: String(r.itemId),
        batchId: String(r.batchId),
        availableQuantity: orderQty,
        quantityReceived: orderQty,
        quantityReturn: 0,
        fromOrder: true,
        itemDisplayName: names.itemName,
        batchDisplayName: names.batchName,
      })
    }

    const orderBonus = await salesOrderService.getOrderBonusLines(orderId)
    for (const bonus of orderBonus) {
      const parent = lines.find((l) => !l.isBonus && Number(l.id) === Number(bonus.orderLineId))
      if (!parent || bonus.quantity <= 0) continue
      const bonusItemId = bonus.itemId
      const bonusBatchId = bonus.batchId
      const names = await resolveItemAndBatchNames(bonusItemId, bonusBatchId, branchId, bonus.quantity)
      if (!itemExtrasMap.has(bonusItemId)) itemExtrasMap.set(bonusItemId, names.itemName)
      const ikey = String(bonusItemId)
      if (!batchExtrasByItem[ikey]) batchExtrasByItem[ikey] = []
      if (bonusBatchId > 0 && !batchExtrasByItem[ikey].some((b) => b.id === bonusBatchId)) {
        batchExtrasByItem[ikey].push({
          id: bonusBatchId,
          name: names.batchName,
          stock: bonus.quantity,
        })
      }
      lines.push({
        id: 0,
        itemId: String(bonusItemId),
        batchId: String(bonusBatchId || ''),
        availableQuantity: bonus.quantity,
        quantityReceived: bonus.quantity,
        quantityReturn: 0,
        fromOrder: true,
        isBonus: true,
        orderLineId: bonus.orderLineId,
        itemDisplayName: bonus.itemName || names.itemName,
        batchDisplayName: names.batchName,
      })
    }

    return {
      lines: sortDcLinesWithBonus(lines),
      itemExtras: [...itemExtrasMap.entries()].map(([id, name]) => ({ id, name })),
      batchExtrasByItem,
    }
  },

  /** Build dropdown extras for saved lines (edit mode) without replacing line data. */
  async enrichLineLookupsFromLines(
    lines: DeliveryChallanLine[],
    branchId: number,
  ): Promise<DcLineLookupExtras> {
    const itemExtrasMap = new Map<number, string>()
    const batchExtrasByItem: DcLineLookupExtras['batchExtrasByItem'] = {}
    const seen = new Set<string>()

    for (const line of lines) {
      const itemId = Number(line.itemId)
      const batchId = Number(line.batchId)
      if (!itemId || !batchId) continue
      const pair = `${itemId}-${batchId}`
      if (seen.has(pair)) continue
      seen.add(pair)

      const names = await resolveItemAndBatchNames(itemId, batchId, branchId, line.availableQuantity || 0)
      if (!itemExtrasMap.has(itemId)) itemExtrasMap.set(itemId, names.itemName)
      const ikey = String(itemId)
      if (!batchExtrasByItem[ikey]) batchExtrasByItem[ikey] = []
      if (!batchExtrasByItem[ikey].some((b) => b.id === batchId)) {
        batchExtrasByItem[ikey].push({
          id: batchId,
          name: names.batchName,
          stock: names.availableQuantity,
        })
      }
    }

    return {
      itemExtras: [...itemExtrasMap.entries()].map(([id, name]) => ({ id, name })),
      batchExtrasByItem,
    }
  },

  /**
   * POST `/api/Dc/Save` — multipart same as Update; use `Id: 0` for new challan.
   */
  async save(payload: DcSavePayload): Promise<void> {
    return postDcMultipart(endpoints.dc.save, 'Dc/Save', payload)
  },

  /**
   * POST `/api/Dc/Update` — same multipart body as Save; `Id` must be the existing delivery challan id.
   */
  async update(payload: DcSavePayload): Promise<void> {
    if (!payload.id || payload.id <= 0) {
      throw new Error('Delivery challan Id is required for update.')
    }
    return postDcMultipart(endpoints.dc.update, 'Dc/Update', payload)
  },

  /**
   * POST `/api/Dc/ConvertToInvoice?dcId=` — create invoice from delivery challan.
   */
  async convertToInvoice(dcId: number): Promise<void> {
    if (!dcId || dcId <= 0) throw new Error('Invalid delivery challan id.')
    const res = await apiClient.post(endpoints.dc.convertToInvoice(dcId))
    throwIfDcResultError(res.data)
  },

  /** POST `/api/Dc/ConvertFromOrder/{orderId}` — create a DC from remaining sales-order qty. */
  async convertFromOrder(orderId: number): Promise<void> {
    if (!orderId || orderId <= 0) throw new Error('Invalid sales order.')
    const res = await apiClient.post(endpoints.dc.convertFromOrder(orderId))
    throwIfDcResultError(res.data)
  },

  /**
   * POST `/api/Dc/MultipleInvoices` — JSON array of delivery challan ids (combined invoice).
   */
  async multipleInvoices(dcIds: number[]): Promise<void> {
    const ids = [...new Set(dcIds.map((id) => Number(id)).filter((id) => id > 0))]
    if (ids.length === 0) throw new Error('Select at least one delivery challan.')
    const res = await apiClient.post(endpoints.dc.multipleInvoices, ids, {
      headers: { 'Content-Type': 'application/json' },
    })
    throwIfDcResultError(res.data)
  },
}
